import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/app/utils/cn";

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-full border font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-all duration-300 ease-in-out overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        "outline-main":
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground [a&]:hover:border-main",
        main: "border-transparent bg-main text-white [a&]:hover:bg-main/90",
        success:
          "border-transparent bg-green-500 text-white [a&]:hover:bg-green-500/90",
        pink: "border-transparent bg-pink-500 text-white [a&]:hover:bg-pink-500/90",
        black: "border-transparent bg-black text-white [a&]:hover:bg-black/90",
        "dark-black":
          "border-transparent bg-gray-800 text-white [a&]:hover:bg-gray-800/90",
        purple:
          "border-transparent bg-purple-600 text-white [a&]:hover:bg-purple-600/90",
        "orange-pink":
          "border-transparent bg-linear-to-r from-orange-500 to-pink-500 text-white [a&]:hover:opacity-90",
        gradient:
          "border-transparent bg-linear-to-r from-purple-600 via-pink-600 to-blue-600 text-white [a&]:hover:opacity-90",
      },
      size: {
        default: "px-2 py-0.5 text-xs",
        sm: "px-1 py-0 text-[10px]",
        lg: "px-5 py-2 font-normal text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Badge({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span";
  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
